import { useState, useCallback, useRef } from 'react';
import { PlayerAchievement } from '../types/achievements';
import { achievementDefinitions } from '../data/achievements';
import { useLocalStorage } from './useLocalStorage';

interface GameStateForCheck {
  correctStreak: number;
  maxStreak: number;
  score: number;
  hearts: number;
  answerTimeSeconds: number;
  lastPointsEarned: number;
  currentQuestionDifficulty: string;
  wasCorrect: boolean;
}

interface GameEndState {
  correctAnswers: number;
  totalQuestions: number;
  questionsAnswered: number;
  totalPoints: number;
  maxStreak: number;
  hearts: number;
  avgAnswerTime: number;
  hardQuestionsCorrect: number;
  hardQuestionsTotal: number;
  gameMode: string;
  loginStreak: number;
}

export function useAchievements() {
  const [achievements, setAchievements] = useLocalStorage<PlayerAchievement[]>(
    'histocracy_achievements',
    [],
  );
  const [toastQueue, setToastQueue] = useState<string[]>([]);
  const sessionUnlocked = useRef<string[]>([]);
  const [totalPointsEver, setTotalPointsEver] = useLocalStorage<number>(
    'histocracy_total_points',
    0,
  );

  const isUnlocked = useCallback(
    (id: string) => achievements.some((a) => a.id === id),
    [achievements],
  );

  const unlock = useCallback(
    (id: string) => {
      if (isUnlocked(id)) return;
      const now = Date.now();
      setAchievements((prev) => [...prev, { id, unlockedAt: now }]);
      setToastQueue((prev) => [...prev, id]);
      sessionUnlocked.current.push(id);
    },
    [isUnlocked, setAchievements],
  );

  const dismissToast = useCallback(() => {
    setToastQueue((prev) => prev.slice(1));
  }, []);

  const checkAfterAnswer = useCallback(
    (state: GameStateForCheck) => {
      if (!state.wasCorrect) return;

      // Streak achievements
      if (state.correctStreak >= 3) unlock('streak_3');
      if (state.correctStreak >= 5) unlock('streak_5');
      if (state.correctStreak >= 10) unlock('streak_10');
      if (state.correctStreak >= 15) unlock('streak_15');

      // Speed achievements
      if (state.answerTimeSeconds < 1) unlock('lightning_answer');

      // Century club
      if (state.lastPointsEarned >= 100) unlock('century_club');

      // Hard first try
      if (state.currentQuestionDifficulty === 'HARD') unlock('hard_first_try');
    },
    [unlock],
  );

  const checkAfterGame = useCallback(
    (state: GameEndState) => {
      // Update total points
      const newTotal = totalPointsEver + state.totalPoints;
      setTotalPointsEver(newTotal);

      // First win
      if (state.hearts > 0) unlock('first_win');

      // Perfect game
      if (state.correctAnswers === state.questionsAnswered && state.questionsAnswered > 0) {
        unlock('perfect_game');
      }

      // Comeback king
      if (state.hearts === 1) unlock('comeback_king');

      // Full hearts
      if (state.hearts >= 5) unlock('full_hearts');

      // Speed demon
      if (state.avgAnswerTime > 0 && state.avgAnswerTime < 2) unlock('speed_demon');

      // Challenge conqueror
      if (state.hardQuestionsTotal > 0 && state.hardQuestionsCorrect === state.hardQuestionsTotal) {
        unlock('challenge_conqueror');
      }

      // Daily achievements
      if (state.gameMode === 'DAILY_CHALLENGE') {
        unlock('daily_first');
        if (state.loginStreak >= 7) unlock('daily_streak_7');
        if (state.loginStreak >= 14) unlock('daily_streak_14');
        if (state.loginStreak >= 30) unlock('daily_streak_30');
      }

      // Milestone achievements
      if (newTotal >= 1000) unlock('points_1000');
      if (newTotal >= 10000) unlock('points_10000');
      if (newTotal >= 50000) unlock('points_50000');
    },
    [unlock, totalPointsEver, setTotalPointsEver],
  );

  return {
    achievements,
    toastQueue,
    dismissToast,
    checkAfterAnswer,
    checkAfterGame,
    sessionUnlocked: sessionUnlocked.current,
    isUnlocked,
  };
}
